import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Icon,
  Input,
  Progress,
  Spin,
  Tag,
  Upload,
  message as antMessage
} from 'antd'
import { ImagePreview } from '../../../../../../components'
import FinalizedReportModal from './FinalizedReportModal'
import './CreativeDirectionChat.scss'

const { TextArea } = Input
const MAX_REFERENCE_FILE_SIZE = 50 * 1024 * 1024

const starterPrompts = [
  {
    key: 'suggest',
    icon: 'star',
    title: 'Suggest for me',
    description: 'AI reads the story and suggests a vision.',
    actionLabel: 'Start',
    recommended: true,
    message:
      'Read my story and suggest a clear visual direction before we generate the shot list.'
  },
  {
    key: 'questions',
    icon: 'question-circle',
    title: 'Ask me questions',
    description: 'AI asks guided questions to shape the look.',
    actionLabel: 'Start',
    action: 'start'
  },
  {
    key: 'known-style',
    icon: 'edit',
    title: 'I know the style',
    description: 'Tell the AI your style and references.',
    actionLabel: 'Start',
    message:
      'I already have a visual style in mind. Ask me only the key questions needed before the shot list.'
  }
]

const PHASE_VIEWS = {
  vision: {
    label: 'Vision',
    navLabel: 'Vision',
    shortLabel: 'Vision',
    icon: 'file-text',
    title: 'Vision',
    description: 'Chat with the AI and save the visual vision.',
    primaryNoun: 'vision',
    tip: 'Save the vision when it is clear enough to guide the next steps.'
  },
  characters: {
    label: 'Characters',
    navLabel: 'Characters',
    shortLabel: 'Characters',
    icon: 'team',
    title: 'Characters',
    description: 'Extract, review, and generate the characters in the story.',
    primaryNoun: 'characters',
    generatedLabel: 'character assets',
    tip: 'Characters may require approval before the next phase opens.'
  },
  outfits: {
    label: 'Outfits',
    navLabel: 'Outfits',
    shortLabel: 'Outfits',
    icon: 'skin',
    title: 'Outfits',
    description: 'Create outfit sheets and visual continuity for the characters.',
    primaryNoun: 'outfits',
    generatedLabel: 'wardrobe sheets',
    tip: 'Generate wardrobe sheets before moving into world building.'
  },
  locations: {
    label: 'Locations',
    navLabel: 'Locations',
    shortLabel: 'Locations',
    icon: 'global',
    title: 'Locations',
    description: 'Build the location boards for the film.',
    primaryNoun: 'locations',
    generatedLabel: 'location boards',
    tip: 'Location counts can include base locations and variants.'
  },
  storyboard: {
    label: 'Storyboard',
    navLabel: 'Storyboard',
    shortLabel: 'Storyboard',
    icon: 'layout',
    title: 'Storyboard',
    description: 'Extract the shot plan, approve it, then generate storyboard sheets.',
    primaryNoun: 'panels',
    generatedLabel: 'storyboard sheets',
    tip: 'Extract and approve the storyboard plan before generating boards.'
  },
  finalized: {
    label: 'Creative Guideline',
    navLabel: 'Creative Guideline',
    shortLabel: 'Guideline',
    icon: 'book',
    title: 'Creative Guideline',
    description: 'Finalize the creative guideline and unlock the shot list.',
    primaryNoun: 'guideline',
    generatedLabel: 'creative guideline',
    tip: 'The next step unlocks after the creative guideline is finalized.'
  }
}

const getPhaseView = phase => PHASE_VIEWS[phase] || PHASE_VIEWS.vision

const getPhaseIndex = (phaseSteps, phase) => {
  const index = phaseSteps.findIndex(item => item.key === phase)
  return index === -1 ? 0 : index
}

const getNextPhaseLabel = (phaseSteps, currentPhase) => {
  const index = getPhaseIndex(phaseSteps, currentPhase)
  const nextPhase = phaseSteps[index + 1]
  return nextPhase ? getPhaseView(nextPhase.key).label : 'Shot List'
}

const getPhaseStatus = ({ phaseSteps, currentPhase, phase, isFinalized }) => {
  const currentIndex = getPhaseIndex(phaseSteps, currentPhase)
  const phaseIndex = getPhaseIndex(phaseSteps, phase)

  if (isFinalized || phaseIndex < currentIndex) return 'complete'
  if (phaseIndex === currentIndex) return 'active'
  return 'pending'
}

const isPhaseSelectable = ({ phaseSteps, currentPhase, phase, isFinalized }) =>
  isFinalized ||
  getPhaseIndex(phaseSteps, phase) <= getPhaseIndex(phaseSteps, currentPhase)

const getStatusLabel = status => {
  if (status === 'complete') return 'Done'
  if (status === 'active') return 'In Progress'
  return 'Pending'
}

const getPhaseCount = (phaseData = {}, phase) => {
  const counts = phaseData.status?.counts || {}
  const countKey = phase === 'storyboard' ? 'panels' : phase
  const savedCount =
    phase === 'storyboard'
      ? phaseData.storyboard?.committedPanels
      : phaseData[phase]?.count

  return savedCount || counts[countKey] || 0
}

const getGeneratedAssets = (phaseData = {}, phase) => {
  const phaseAssets = phaseData[phase]?.generated
  if (Array.isArray(phaseAssets)) return phaseAssets
  if (Array.isArray(phaseAssets?.generated)) {
    return phaseAssets.generated
  }
  if (Array.isArray(phaseAssets?.assets)) {
    return phaseAssets.assets
  }

  return []
}

const isStoryboardPlanApproved = (phaseData = {}) =>
  Boolean(
    phaseData.storyboard?.planApproved ||
      phaseData.storyboard?.committedPanels ||
      phaseData.status?.storyboardPlanApproved
  )

const getStoryboardPlanPayload = plan => {
  if (!plan || typeof plan !== 'object') return {}
  return plan.plan || plan.storyboard_plan || plan.storyboardPlan || plan
}

const getStoryboardPlanShots = plan => {
  const payload = getStoryboardPlanPayload(plan)
  const candidates = [
    payload.shots,
    payload.panels,
    payload.storyboard,
    payload.plan?.shots,
    payload.storyboard_plan?.shots
  ]

  return candidates.find(Array.isArray) || []
}

const getStoryboardPlanCount = plan => {
  const payload = getStoryboardPlanPayload(plan)
  const shots = getStoryboardPlanShots(plan)
  return (
    payload.proposed_count ||
    payload.proposedCount ||
    payload.committed_panels ||
    payload.committedPanels ||
    shots.length ||
    0
  )
}

const getStoryboardPlanRationale = plan => {
  const payload = getStoryboardPlanPayload(plan)
  return (
    payload.rationale ||
    payload.description ||
    payload.summary ||
    payload.notes ||
    ''
  )
}

const formatPlanValue = value => {
  if (Array.isArray(value)) return value.filter(Boolean).join(', ')
  if (value === undefined || value === null) return ''
  return String(value)
}

const formatCount = value => {
  const count = Number(value || 0)
  return Number.isFinite(count) ? count : 0
}

const getFileExtension = fileName => {
  const parts = String(fileName || '').split('.')
  return parts.length > 1 ? parts.pop().toLowerCase() : ''
}

const isAcceptedReferenceFile = (file, acceptedExtensions = []) => {
  const extension = getFileExtension(file?.name)
  return acceptedExtensions.map(item => item.toLowerCase()).includes(extension)
}

const getReferenceKind = file => {
  if (String(file?.type || '').startsWith('video/')) return 'video'
  if (String(file?.type || '').startsWith('image/')) return 'image'

  const extension = getFileExtension(file?.name)
  return ['mp4', 'mov', 'webm'].includes(extension) ? 'video' : 'image'
}

const formatFileSize = size => {
  const fileSize = Number(size || 0)
  if (!fileSize) return ''
  if (fileSize < 1024 * 1024) return `${Math.ceil(fileSize / 1024)} KB`
  return `${(fileSize / (1024 * 1024)).toFixed(1)} MB`
}

const getAcceptedAttribute = acceptedExtensions =>
  (acceptedExtensions || []).map(ext => `.${ext}`).join(',')

const stripLooseMarkdownMarkers = value =>
  String(value || '')
    .replace(/\*\*/g, '')
    .replace(/\*/g, '')

const splitLongParagraph = line => {
  const sourceText = String(line || '').trim()

  if (sourceText.length <= 420) return [sourceText]

  const text = stripLooseMarkdownMarkers(sourceText)

  const sentences = text.match(/[^.!?]+[.!?]+["')\]]*|[^.!?]+$/g)
  if (!sentences || sentences.length <= 1) return [text]

  const chunks = []
  let currentChunk = ''

  sentences.forEach(sentence => {
    const cleanSentence = sentence.trim()
    if (!cleanSentence) return

    const nextChunk = currentChunk
      ? `${currentChunk} ${cleanSentence}`
      : cleanSentence

    if (nextChunk.length > 360 && currentChunk) {
      chunks.push(currentChunk)
      currentChunk = cleanSentence
      return
    }

    currentChunk = nextChunk
  })

  if (currentChunk) chunks.push(currentChunk)

  return chunks.length ? chunks : [text]
}

const revokePendingReferencePreviews = references => {
  (references || []).forEach(item => {
    if (item.previewUrl && window.URL?.revokeObjectURL) {
      window.URL.revokeObjectURL(item.previewUrl)
    }
  })
}

const getFriendlyErrorMessage = error => {
  const text = String(error || '')

  if (/value too long|character varying\(200\)/i.test(text)) {
    return 'This phase hit a backend text limit. Shorten the vision, then try again.'
  }

  if (/all characters.*verified|verify all characters/i.test(text)) {
    return 'Approve every character before continuing to wardrobe.'
  }

  if (/creative statement/i.test(text) && /locked|set|required/i.test(text)) {
    return 'Save a concise vision before continuing.'
  }

  return text
}

const renderInlineFormatting = text => {
  const parts = String(text || '').split(
    /(`[^`]+`|\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*|\*[^*\n]+\*)/g
  )

  return parts.map((part, index) => {
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/)
    if (linkMatch) {
      const href = linkMatch[2]
      const isSafeHref = /^https?:\/\//i.test(href)

      return isSafeHref ? (
        <a
          href={href}
          key={`${part}-${index}`}
          rel="noopener noreferrer"
          target="_blank"
        >
          {linkMatch[1]}
        </a>
      ) : (
        linkMatch[1]
      )
    }

    if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={`${part}-${index}`}>{part.slice(1, -1)}</code>
    }

    if (part.startsWith('**') && part.endsWith('**')) {
      const content = part.slice(2, -2)
      if (content.length > 140) return stripLooseMarkdownMarkers(content)
      return <strong key={`${part}-${index}`}>{content}</strong>
    }

    if (part.startsWith('*') && part.endsWith('*')) {
      const content = part.slice(1, -1)
      if (content.length > 140) return stripLooseMarkdownMarkers(content)
      return <em key={`${part}-${index}`}>{content}</em>
    }

    return stripLooseMarkdownMarkers(part)
  })
}

const renderFormattedMessage = text => {
  const lines = String(text || '').split('\n')
  const blocks = []
  let listItems = []
  let listType = 'ul'
  let codeLines = []
  let isInCodeBlock = false

  const flushList = () => {
    if (!listItems.length) return

    const ListTag = listType
    blocks.push(
      <ListTag
        className="creative-direction-chat__content-list"
        key={`list-${blocks.length}`}
      >
        {listItems.map((item, index) => (
          <li key={`${item}-${index}`}>{renderInlineFormatting(item)}</li>
        ))}
      </ListTag>
    )
    listItems = []
    listType = 'ul'
  }

  const flushCodeBlock = () => {
    if (!codeLines.length) return

    blocks.push(
      <pre
        className="creative-direction-chat__code-block"
        key={`code-${blocks.length}`}
      >
        <code>{codeLines.join('\n')}</code>
      </pre>
    )
    codeLines = []
  }

  lines.forEach((line, index) => {
    const trimmedLine = line.trim()

    if (trimmedLine.startsWith('```')) {
      flushList()

      if (isInCodeBlock) {
        isInCodeBlock = false
        flushCodeBlock()
      } else {
        isInCodeBlock = true
        codeLines = []
      }

      return
    }

    if (isInCodeBlock) {
      codeLines.push(line)
      return
    }

    if (!trimmedLine) {
      flushList()
      return
    }

    const bulletMatch = trimmedLine.match(/^[-*]\s+(.+)$/)
    const numberedMatch = trimmedLine.match(/^\d+\.\s+(.+)$/)
    const headingMatch = trimmedLine.match(/^#{1,4}\s*(.+)$/)
    const quoteMatch = trimmedLine.match(/^>\s?(.+)$/)
    const dividerMatch = trimmedLine.match(/^(-{3,}|\*{3,}|_{3,})$/)

    if (headingMatch && headingMatch[1].length <= 120) {
      flushList()
      blocks.push(
        <h4 key={`heading-${index}`}>
          {renderInlineFormatting(headingMatch[1])}
        </h4>
      )
      return
    }

    if (headingMatch) {
      flushList()
      splitLongParagraph(headingMatch[1]).forEach((paragraph, paragraphIndex) => {
        blocks.push(
          <p key={`heading-paragraph-${index}-${paragraphIndex}`}>
            {renderInlineFormatting(paragraph)}
          </p>
        )
      })
      return
    }

    if (quoteMatch) {
      flushList()
      blocks.push(
        <blockquote key={`quote-${index}`}>
          {renderInlineFormatting(quoteMatch[1])}
        </blockquote>
      )
      return
    }

    if (dividerMatch) {
      flushList()
      blocks.push(<hr key={`divider-${index}`} />)
      return
    }

    if (bulletMatch) {
      if (listItems.length && listType !== 'ul') flushList()
      listType = 'ul'
      listItems.push(bulletMatch[1])
      return
    }

    if (numberedMatch) {
      if (listItems.length && listType !== 'ol') flushList()
      listType = 'ol'
      listItems.push(numberedMatch[1])
      return
    }

    flushList()
    splitLongParagraph(trimmedLine).forEach((paragraph, paragraphIndex) => {
      blocks.push(
        <p key={`line-${index}-${paragraphIndex}`}>
          {renderInlineFormatting(paragraph)}
        </p>
      )
    })
  })

  flushList()
  if (isInCodeBlock) {
    flushCodeBlock()
  }

  return blocks
}

const ProgressPreview = ({
  phaseSteps,
  currentPhase,
  expandedPhases,
  progressPercent,
  isFinalized,
  onPhaseSelect
}) => {
  return (
    <aside className="creative-direction-chat__preview">
      <section className="creative-direction-chat__preview-card">
        <div className="creative-direction-chat__preview-header">
          <Icon type="eye" />
          <div>
            <h3>Creative Guideline Preview</h3>
            <p>A live view of what is ready for the shot list.</p>
          </div>
        </div>

        <div className="creative-direction-chat__progress-row">
          <span>Overall Progress</span>
          <strong>{progressPercent}%</strong>
        </div>
        <Progress
          percent={progressPercent}
          strokeColor="#5057d5"
          trailColor="#e8edf5"
          size="small"
          showInfo={false}
        />

        <div className="creative-direction-chat__workflow-label">Workflow</div>
        <div className="creative-direction-chat__phase-status-list">
          {phaseSteps.map((step, index) => {
            const view = getPhaseView(step.key)
            const status = getPhaseStatus({
              phaseSteps,
              currentPhase,
              phase: step.key,
              isFinalized
            })
            const canSelect = isPhaseSelectable({
              phaseSteps,
              currentPhase,
              phase: step.key,
              isFinalized
            })
            const isSelected = Boolean(expandedPhases[step.key])

            return (
              <button
                type="button"
                className={`creative-direction-chat__phase-status creative-direction-chat__phase-status--${status}${
                  isSelected
                    ? ' creative-direction-chat__phase-status--selected'
                    : ''
                }`}
                disabled={!canSelect}
                key={step.key}
                onClick={() => canSelect && onPhaseSelect(step.key)}
              >
                <span className="creative-direction-chat__phase-number">
                  {index + 1}
                </span>
                <span>{view.shortLabel}</span>
                <strong>{getStatusLabel(status)}</strong>
              </button>
            )
          })}
        </div>
      </section>
    </aside>
  )
}

const StageHeader = () => {
  return (
    <div className="creative-direction-chat__stage-header">
      <div>
        <h2>Chat</h2>
        <p>Continue the conversation and review each completed step below.</p>
      </div>
    </div>
  )
}

const getMessagePhase = message => message?.phase || 'vision'

const MessageList = ({ messages, showTyping = false }) => {
  if (!messages.length && !showTyping) return null

  return (
    <div className="creative-direction-chat__phase-messages">
      {messages.map(item => (
        <div
          className={`creative-direction-chat__message creative-direction-chat__message--${item.role}`}
          key={item.id}
        >
          <div className="creative-direction-chat__avatar">
            <Icon type={item.role === 'assistant' ? 'robot' : 'user'} />
          </div>
          <div className="creative-direction-chat__bubble">
            <div className="creative-direction-chat__role">
              {item.role === 'assistant' ? 'AI' : 'You'}
            </div>
            <div className="creative-direction-chat__content">
              {item.role === 'assistant'
                ? renderFormattedMessage(item.content)
                : item.content}
              {item.streaming ? (
                <span className="creative-direction-chat__cursor" />
              ) : null}
            </div>
          </div>
        </div>
      ))}

      {showTyping ? (
        <div className="creative-direction-chat__message creative-direction-chat__message--assistant">
          <div className="creative-direction-chat__avatar">
            <Icon type="robot" />
          </div>
          <div className="creative-direction-chat__bubble">
            <div className="creative-direction-chat__role">AI</div>
            <div className="creative-direction-chat__typing">
              <span />
              <span />
              <span />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

const VisionDirectionCard = ({
  creativeStatement,
  hasConversation,
  isStreaming,
  isSynthesizingVision,
  isLockingVision,
  isVisionLocked,
  isArchived,
  onSynthesizeVision,
  onLockVision
}) => {
  const hasStatement = Boolean(String(creativeStatement || '').trim())
  const shouldShowSynthesis = !isArchived && isSynthesizingVision

  if (!isArchived && (isStreaming || !hasConversation)) return null

  if (isArchived && !hasStatement) return null

  return (
    <section
      className={`creative-direction-chat__decision-panel${
        isArchived ? ' creative-direction-chat__decision-panel--archived' : ''
      }`}
    >
      <div className="creative-direction-chat__card-heading">
        <Icon type="star" />
        <div>
          <h3>Vision statement</h3>
          <p>
            {isArchived
              ? 'Locked and ready to guide the remaining creative guideline.'
              : 'Create and review the vision before moving to characters.'}
          </p>
        </div>
        {isVisionLocked ? <Tag color="green">Saved</Tag> : null}
      </div>

      {shouldShowSynthesis ? (
        <div className="creative-direction-chat__inline-loading">
          <Spin />
          <span>Synthesizing vision from the conversation...</span>
        </div>
      ) : (
        <>
          {hasStatement ? (
            <div className="creative-direction-chat__vision-statement">
              {creativeStatement}
            </div>
          ) : (
            <div className="creative-direction-chat__vision-empty">
              <Icon type="info-circle" />
              <span>
                When the conversation feels ready, create the vision statement.
              </span>
            </div>
          )}

          {!isArchived ? (
            <div className="creative-direction-chat__action-row">
              <Button
                icon={hasStatement ? 'reload' : 'star'}
                loading={isSynthesizingVision}
                disabled={
                  isStreaming ||
                  isVisionLocked ||
                  isLockingVision ||
                  !onSynthesizeVision
                }
                onClick={() => onSynthesizeVision({ showError: true })}
              >
                {hasStatement
                  ? 'Update vision statement'
                  : 'Create vision statement'}
              </Button>
              <Button
                type="primary"
                icon="check-circle"
                loading={isLockingVision}
                disabled={isStreaming || isVisionLocked || !hasStatement}
                onClick={onLockVision}
              >
                Lock vision
              </Button>
            </div>
          ) : null}
        </>
      )}
    </section>
  )
}

const WorkflowStepDivider = ({ stepNumber, label }) => (
  <div className="creative-direction-chat__step-divider">
    <span />
    <strong>{`Step ${stepNumber}: ${label}`}</strong>
    <span />
  </div>
)

const AssetPreview = ({ assets, emptyLabel, limit = 4 }) => (
  <div className="creative-direction-chat__asset-preview">
    {assets.length ? (
      assets.slice(0, limit).map((asset, index) => (
        <div
          className="creative-direction-chat__asset-tile"
          key={asset.id || asset.image_url || index}
        >
          {asset.image_url || asset.thumbnail_url ? (
            <ImagePreview
              src={asset.image_url || asset.thumbnail_url}
              alt={asset.asset_type || emptyLabel}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <Icon type="picture" />
          )}
          <span>{asset.generation_status || 'Generated'}</span>
        </div>
      ))
    ) : (
      <>
        {[0, 1, 2].map(index => (
          <div className="creative-direction-chat__asset-tile" key={index}>
            <Icon type="picture" />
            <span>{emptyLabel}</span>
          </div>
        ))}
      </>
    )}
  </div>
)

const getEntityKey = (entity, index) =>
  entity.id || entity.character_key || entity.name || index

const getEntityApiId = entity =>
  entity?.id || entity?.entity_id || entity?.entityId || entity?.db_id || null

const isEntityVerified = entity =>
  Boolean(entity?.user_verified || entity?.verified || entity?.is_verified)

const getEntityDescription = entity =>
  entity.physical_description ||
  entity.description ||
  entity.role_in_story ||
  entity.character_arc ||
  entity.voice_description ||
  entity.scene_context ||
  'Ready for review'

const EntityListPreview = ({
  entities,
  phase,
  isActivePhase,
  phaseBusyAction,
  onVerifyEntity
}) => {
  const [expandedEntityKeys, setExpandedEntityKeys] = useState({})

  if (!entities.length) return null

  const toggleDescription = entityKey => {
    setExpandedEntityKeys(prev => ({
      ...prev,
      [entityKey]: !prev[entityKey]
    }))
  }

  return (
    <div className="creative-direction-chat__entity-list">
      {entities.map((entity, index) => {
        const entityKey = getEntityKey(entity, index)
        const description = getEntityDescription(entity)
        const isExpanded = Boolean(expandedEntityKeys[entityKey])
        const canExpand = description.length > 160
        const entityApiId = getEntityApiId(entity)
        const isVerified = isEntityVerified(entity)
        const isVerifying =
          phaseBusyAction === `verify:${phase}:${entityApiId}`

        return (
          <div
            className="creative-direction-chat__entity-item"
            key={entityKey}
          >
            <div>
              <strong>
                {entity.name || entity.placeholder || `Item ${index + 1}`}
              </strong>
              <span
                className={`creative-direction-chat__entity-description${
                  isExpanded
                    ? ' creative-direction-chat__entity-description--expanded'
                    : ''
                }`}
              >
                {description}
              </span>
              {canExpand ? (
                <button
                  type="button"
                  className="creative-direction-chat__entity-read-more"
                  aria-expanded={isExpanded}
                  onClick={() => toggleDescription(entityKey)}
                >
                  {isExpanded ? 'Show less' : 'Read more'}
                </button>
              ) : null}
            </div>
            {isVerified ? (
              <span className="creative-direction-chat__entity-status creative-direction-chat__entity-status--verified">
                <Icon type="check-circle" />
                Verified
              </span>
            ) : isActivePhase ? (
              <Button
                size="small"
                icon="check-circle"
                loading={isVerifying}
                disabled={
                  !entityApiId || Boolean(phaseBusyAction) || !onVerifyEntity
                }
                onClick={() => onVerifyEntity(phase, entity)}
              >
                Verify
              </Button>
            ) : (
              <span className="creative-direction-chat__entity-status">
                <Icon type="clock-circle" />
                Waiting
              </span>
            )}
          </div>
        )
      })}
    </div>
  )
}

const getEntityPrimaryAction = ({
  currentPhase,
  phaseData,
  phaseSteps,
  onExtractPhase,
  onVerifyAllPhase,
  onGeneratePhase,
  onAdvancePhase
}) => {
  const view = getPhaseView(currentPhase)
  const currentPhaseData = phaseData[currentPhase] || {}
  const assets = getGeneratedAssets(phaseData, currentPhase)
  const hasExtracted = Boolean(currentPhaseData.extracted || currentPhaseData.count)
  const hasGenerated = Boolean(assets.length || currentPhaseData.generated?.length)
  const entities = currentPhaseData.entities || []
  const allEntitiesVerified =
    !entities.length || entities.every(isEntityVerified)

  if (!hasExtracted) {
    return {
      label: `Extract ${view.primaryNoun}`,
      icon: 'scan',
      loadingKey: `extract:${currentPhase}`,
      onClick: onExtractPhase
    }
  }

  if (!allEntitiesVerified) {
    return {
      label: `Verify all ${view.primaryNoun}`,
      icon: 'check-circle',
      loadingKey: `verify-all:${currentPhase}`,
      disabled: !onVerifyAllPhase,
      onClick: () => onVerifyAllPhase(currentPhase)
    }
  }

  if (!hasGenerated) {
    return {
      label: `Generate ${view.generatedLabel}`,
      icon: 'picture',
      loadingKey: `generate:${currentPhase}`,
      onClick: onGeneratePhase
    }
  }

  return {
    label: `Continue to ${getNextPhaseLabel(phaseSteps, currentPhase)}`,
    icon: 'arrow-right',
    loadingKey: `advance:${currentPhase}`,
    onClick: onAdvancePhase
  }
}

const getStoryboardPrimaryAction = ({
  phaseData,
  phaseSteps,
  onGeneratePhase,
  onAdvancePhase,
  onProposeStoryboardPlan,
  onApproveStoryboardPlan
}) => {
  const storyboardData = phaseData.storyboard || {}
  const assets = getGeneratedAssets(phaseData, 'storyboard')
  const hasPlan = Boolean(storyboardData.plan)
  const planApproved = isStoryboardPlanApproved(phaseData)
  const hasGenerated = Boolean(assets.length || storyboardData.generated?.length)

  if (!hasPlan) {
    return {
      label: 'Extract plan',
      icon: 'profile',
      loadingKey: 'storyboard:propose',
      onClick: onProposeStoryboardPlan
    }
  }

  if (!planApproved) {
    return {
      label: 'Approve shot plan',
      icon: 'check',
      loadingKey: 'storyboard:approve',
      onClick: onApproveStoryboardPlan
    }
  }

  if (!hasGenerated) {
    return {
      label: 'Generate storyboard',
      icon: 'picture',
      loadingKey: 'generate:storyboard',
      onClick: onGeneratePhase
    }
  }

  return {
    label: `Continue to ${getNextPhaseLabel(phaseSteps, 'storyboard')}`,
    icon: 'arrow-right',
    loadingKey: 'advance:storyboard',
    onClick: onAdvancePhase
  }
}

const getFinalPrimaryAction = ({ isFinalized, onFinalizeCreativeDirection }) =>
  isFinalized
    ? null
    : {
        label: 'Generate Finalized Report',
        icon: 'book',
        loadingKey: 'finalize',
        onClick: onFinalizeCreativeDirection
      }

const getPhasePrimaryAction = ({
  currentPhase,
  phaseData,
  phaseSteps,
  isFinalized,
  onExtractPhase,
  onVerifyAllPhase,
  onGeneratePhase,
  onAdvancePhase,
  onProposeStoryboardPlan,
  onApproveStoryboardPlan,
  onFinalizeCreativeDirection
}) => {
  const isEntityPhase = ['characters', 'outfits', 'locations'].includes(
    currentPhase
  )

  if (isEntityPhase) {
    return getEntityPrimaryAction({
      currentPhase,
      phaseData,
      phaseSteps,
      onExtractPhase,
      onVerifyAllPhase,
      onGeneratePhase,
      onAdvancePhase
    })
  }

  if (currentPhase === 'storyboard') {
    return getStoryboardPrimaryAction({
      phaseData,
      phaseSteps,
      onGeneratePhase,
      onAdvancePhase,
      onProposeStoryboardPlan,
      onApproveStoryboardPlan
    })
  }

  if (currentPhase === 'finalized') {
    return getFinalPrimaryAction({
      isFinalized,
      onFinalizeCreativeDirection
    })
  }

  return null
}

const getPhaseSecondaryActions = ({
  currentPhase,
  phaseData,
  onExtractPhase,
  onProposeStoryboardPlan,
  onGeneratePhase
}) => {
  const currentPhaseData = phaseData[currentPhase] || {}
  const assets = getGeneratedAssets(phaseData, currentPhase)
  const hasGeneratedAssets = assets.length > 0
  const hasExtracted = Boolean(
    currentPhaseData.extracted || currentPhaseData.count
  )

  if (
    ['characters', 'outfits', 'locations'].includes(currentPhase) &&
    hasExtracted &&
    !hasGeneratedAssets
  ) {
    return [
      {
        label: 'Extract again',
        icon: 'reload',
        loadingKey: `extract:${currentPhase}`,
        onClick: onExtractPhase
      }
    ]
  }

  if (
    currentPhase === 'storyboard' &&
    currentPhaseData.plan &&
    !hasGeneratedAssets
  ) {
    return [
      {
        label: 'Regenerate plan',
        icon: 'reload',
        loadingKey: 'storyboard:propose',
        onClick: onProposeStoryboardPlan
      }
    ]
  }

  if (currentPhase === 'storyboard' && hasGeneratedAssets) {
    return [
      {
        label: 'Regenerate storyboard',
        icon: 'reload',
        loadingKey: 'generate:storyboard',
        onClick: () => onGeneratePhase('storyboard')
      }
    ]
  }

  return []
}

const getPhaseSummary = ({
  phase,
  phaseData,
  isFinalized
}) => {
  const phasePayload = phaseData[phase] || {}
  const assets = getGeneratedAssets(phaseData, phase)
  const count = getPhaseCount(phaseData, phase)

  if (phase === 'storyboard') {
    const plannedShots = getStoryboardPlanCount(phasePayload.plan)
    const planApproved = isStoryboardPlanApproved(phaseData)

    if (assets.length) {
      return `${formatCount(assets.length)} storyboard visual${
        formatCount(assets.length) === 1 ? '' : 's'
      } generated`
    }

    if (plannedShots) {
      return `${formatCount(plannedShots)} planned shot${
        formatCount(plannedShots) === 1 ? '' : 's'
      }${planApproved ? ' approved' : ' ready for review'}`
    }

    return 'Shot plan not extracted yet'
  }

  if (phase === 'finalized') {
    return isFinalized ? 'Creative guideline ready' : 'Waiting for finalization'
  }

  if (assets.length) {
    return `${formatCount(assets.length)} visual${
      formatCount(assets.length) === 1 ? '' : 's'
    } generated`
  }

  if (count) {
    const entities = phasePayload.entities || []
    const verifiedCount = entities.filter(isEntityVerified).length
    return `${formatCount(count)} item${
      formatCount(count) === 1 ? '' : 's'
    } found · ${formatCount(verifiedCount)} verified`
  }

  return 'Not started'
}

const PhaseSummaryCard = ({
  phase,
  phaseData,
  isFinalized,
  onTogglePhase
}) => {
  const view = getPhaseView(phase)
  const summary = getPhaseSummary({ phase, phaseData, isFinalized })

  return (
    <section className="creative-direction-chat__phase-summary">
      <div className="creative-direction-chat__phase-summary-icon">
        <Icon type={view.icon} />
      </div>
      <div>
        <span>{view.label}</span>
        <strong>{summary}</strong>
      </div>
      <Button size="small" onClick={onTogglePhase}>
        View details
      </Button>
    </section>
  )
}

const VisionSummaryCard = ({
  creativeStatement,
  isVisionLocked,
  onTogglePhase
}) => (
  <section className="creative-direction-chat__phase-summary">
    <div className="creative-direction-chat__phase-summary-icon">
      <Icon type="star" />
    </div>
    <div>
      <span>Vision</span>
      <strong>
        {isVisionLocked
          ? 'Vision locked'
          : creativeStatement
          ? 'Vision statement ready'
          : 'Vision conversation started'}
      </strong>
    </div>
    <Button size="small" onClick={onTogglePhase}>
      View details
    </Button>
  </section>
)

const StoryboardPlanPreview = ({ plan, committedPanels, planApproved }) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const shots = getStoryboardPlanShots(plan)
  const count = getStoryboardPlanCount(plan)
  const rationale = getStoryboardPlanRationale(plan)
  const visibleShots = isExpanded ? shots : shots.slice(0, 4)

  if (!plan) return null

  return (
    <div className="creative-direction-chat__storyboard-plan">
      <div className="creative-direction-chat__storyboard-plan-header">
        <div>
          <span>Shot plan</span>
          <strong>
            {formatCount(committedPanels || count)} planned{' '}
            {formatCount(committedPanels || count) === 1 ? 'shot' : 'shots'}
          </strong>
        </div>
        <Tag color={planApproved ? 'green' : 'blue'}>
          {planApproved ? 'Approved' : 'Review'}
        </Tag>
      </div>

      {rationale ? (
        <p className="creative-direction-chat__storyboard-rationale">
          {rationale}
        </p>
      ) : null}

      {shots.length ? (
        <div className="creative-direction-chat__storyboard-shot-list">
          {visibleShots.map((shot, index) => {
            const shotNumber = shot.panel_number || shot.panelNumber || index + 1
            const title =
              shot.beat ||
              shot.title ||
              shot.name ||
              shot.summary ||
              `Shot ${shotNumber}`
            const summary =
              shot.summary && shot.summary !== title ? shot.summary : ''
            const meta = [
              formatPlanValue(shot.shot_type || shot.shotType),
              formatPlanValue(shot.camera_angle || shot.cameraAngle),
              formatPlanValue(shot.camera_movement || shot.cameraMovement),
              formatPlanValue(shot.location_key || shot.locationKey),
              formatPlanValue(shot.character_keys || shot.characterKeys),
              formatPlanValue(shot.outfit_keys || shot.outfitKeys)
            ].filter(Boolean)

            return (
              <div
                className="creative-direction-chat__storyboard-shot"
                key={`${shotNumber}-${index}`}
              >
                <span>{shotNumber}</span>
                <div>
                  <strong>{title}</strong>
                  {summary ? <p>{summary}</p> : null}
                  {meta.length ? (
                    <small>{meta.join(' · ')}</small>
                  ) : null}
                </div>
              </div>
            )
          })}
          {shots.length > visibleShots.length ? (
            <button
              type="button"
              className="creative-direction-chat__storyboard-show-more"
              onClick={() => setIsExpanded(true)}
            >
              View full plan
              <Icon type="down" />
            </button>
          ) : null}
          {isExpanded && shots.length > 4 ? (
            <button
              type="button"
              className="creative-direction-chat__storyboard-show-more"
              onClick={() => setIsExpanded(false)}
            >
              Show less
              <Icon type="up" />
            </button>
          ) : null}
        </div>
      ) : (
        <div className="creative-direction-chat__quiet-note">
          <Icon type="info-circle" />
          <span>The plan was created, but no shot rows were returned.</span>
        </div>
      )}
    </div>
  )
}

const PhaseWorkspace = ({
  currentPhase,
  phaseSteps,
  phaseData,
  creativeGuideline,
  isFinalized,
  isActivePhase,
  isSelectedPhase,
  showActions = true,
  phaseBusyAction,
  onExtractPhase,
  onVerifyEntity,
  onVerifyAllPhase,
  onGeneratePhase,
  onRefreshGenerationStatus,
  onAdvancePhase,
  onProposeStoryboardPlan,
  onApproveStoryboardPlan,
  onFinalizeCreativeDirection,
  onViewFinalizedReport,
  onTogglePhase
}) => {
  const view = getPhaseView(currentPhase)
  const isBusy = Boolean(phaseBusyAction)
  const currentPhaseData = phaseData[currentPhase] || {}
  const count = getPhaseCount(phaseData, currentPhase)
  const assets = getGeneratedAssets(phaseData, currentPhase)
  const hasGeneratedAssets = assets.length > 0
  const isEntityPhase = ['characters', 'outfits', 'locations'].includes(
    currentPhase
  )
  const isStoryboardPhase = currentPhase === 'storyboard'
  const isFinalPhase = currentPhase === 'finalized'
  const hasFinalizedReport = isFinalPhase && isFinalized && creativeGuideline
  const shouldShowFullPhase = isActivePhase || isSelectedPhase
  const canShowInactiveStoryboardActions =
    isStoryboardPhase && isSelectedPhase && hasGeneratedAssets

  const primaryAction = useMemo(() => {
    if (!isActivePhase) return null

    return getPhasePrimaryAction({
      currentPhase,
      phaseData,
      phaseSteps,
      isFinalized,
      onExtractPhase,
      onVerifyAllPhase,
      onGeneratePhase,
      onAdvancePhase,
      onProposeStoryboardPlan,
      onApproveStoryboardPlan,
      onFinalizeCreativeDirection
    })
  }, [
    currentPhase,
    isFinalized,
    isActivePhase,
    onAdvancePhase,
    onApproveStoryboardPlan,
    onExtractPhase,
    onFinalizeCreativeDirection,
    onGeneratePhase,
    onProposeStoryboardPlan,
    onVerifyAllPhase,
    phaseData,
    phaseSteps
  ])
  const secondaryActions = useMemo(
    () =>
      isActivePhase || canShowInactiveStoryboardActions
        ? getPhaseSecondaryActions({
            currentPhase,
            phaseData,
            onExtractPhase,
            onProposeStoryboardPlan,
            onGeneratePhase
          })
        : [],
    [
      currentPhase,
      canShowInactiveStoryboardActions,
      isActivePhase,
      onExtractPhase,
      onProposeStoryboardPlan,
      onGeneratePhase,
      phaseData
    ]
  )

  const taskTitle = isFinalPhase
    ? isFinalized
      ? 'Creative guideline is ready'
      : 'Ready to build the creative guideline'
    : isStoryboardPhase && !currentPhaseData.plan
    ? 'Extract storyboard plan'
    : isStoryboardPhase && currentPhaseData.plan
    ? 'Review shot plan'
    : count
    ? `AI found ${formatCount(count)} ${view.primaryNoun}`
    : `Prepare ${view.primaryNoun}`

  const taskDescription = isFinalPhase
    ? 'Finalize everything into the document used by the shot list step.'
    : !isActivePhase
    ? 'You are reviewing this completed step. The current task remains below in the conversation.'
    : isStoryboardPhase
    ? 'Extract the shot plan first. If it looks right, approve it and render the storyboard sheets.'
    : 'Run each step in order. Sharelook will stop you only when something still needs approval.'
  if (!shouldShowFullPhase) {
    return (
      <PhaseSummaryCard
        phase={currentPhase}
        phaseData={phaseData}
        isFinalized={isFinalized}
        onTogglePhase={onTogglePhase}
      />
    )
  }

  return (
    <section className="creative-direction-chat__decision-panel creative-direction-chat__phase-workspace">
      <div className="creative-direction-chat__phase-hero">
        <div className="creative-direction-chat__phase-icon">
          <Icon type={view.icon} />
        </div>
        <div>
          <h3>{taskTitle}</h3>
          <p>{taskDescription}</p>
        </div>
        {isSelectedPhase && !isActivePhase ? (
          <Button size="small" onClick={onTogglePhase}>
            Hide details
          </Button>
        ) : !primaryAction ? (
          <Tag color="green">Complete</Tag>
        ) : null}
      </div>

      {isStoryboardPhase && currentPhaseData.plan ? (
        <StoryboardPlanPreview
          plan={currentPhaseData.plan}
          committedPanels={currentPhaseData.committedPanels}
          planApproved={isStoryboardPlanApproved(phaseData)}
        />
      ) : null}

      <EntityListPreview
        entities={currentPhaseData.entities || []}
        phase={currentPhase}
        isActivePhase={isActivePhase}
        phaseBusyAction={phaseBusyAction}
        onVerifyEntity={onVerifyEntity}
      />

      {!isFinalPhase && hasGeneratedAssets ? (
        <>
          <div className="creative-direction-chat__subheading">
            <span>Generated visuals</span>
            <Button
              size="small"
              icon="sync"
              disabled={isBusy || !isActivePhase}
              loading={phaseBusyAction === 'generation-status'}
              onClick={onRefreshGenerationStatus}
            >
              Refresh
            </Button>
          </div>
          <AssetPreview
            assets={assets}
            emptyLabel={view.generatedLabel || 'Pending'}
            limit={currentPhase === 'characters' ? 6 : 4}
          />
        </>
      ) : null}

      {isEntityPhase && currentPhaseData.count ? (
        <div className="creative-direction-chat__quiet-note">
          <Icon type="info-circle" />
          <span>
            Verify all items, or review them one by one before generating visuals.
          </span>
        </div>
      ) : null}

      {showActions &&
      (primaryAction || secondaryActions.length || hasFinalizedReport) ? (
        <div className="creative-direction-chat__phase-action-row">
          {hasFinalizedReport ? (
            <Button icon="profile" onClick={onViewFinalizedReport}>
              View Finalized Report
            </Button>
          ) : null}
          {secondaryActions.map(action => (
            <Button
              key={action.loadingKey || action.label}
              icon={action.icon}
              loading={phaseBusyAction === action.loadingKey}
              disabled={isBusy && phaseBusyAction !== action.loadingKey}
              onClick={action.onClick}
            >
              {action.label}
            </Button>
          ))}
          {primaryAction ? (
            <Button
              type="primary"
              icon={primaryAction.icon}
              loading={
                (primaryAction.loadingKey &&
                  phaseBusyAction === primaryAction.loadingKey) ||
                phaseBusyAction.endsWith(`:${currentPhase}`)
              }
              disabled={
                primaryAction.disabled ||
                (isBusy && phaseBusyAction !== primaryAction.loadingKey)
              }
              onClick={primaryAction.onClick}
            >
              {primaryAction.label}
            </Button>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}

const ConversationCard = ({
  sessionId,
  messages,
  timelinePanel,
  scrollRef,
  errorMessage,
  isCreatingSession,
  isStreaming,
  isSynthesizingVision,
  isUploadingReference,
  acceptedExtensions,
  pendingReferences,
  draftMessage,
  onDraftMessageChange,
  onAttachReference,
  onRemovePendingReference,
  onSubmit,
  onKeyDown,
  onStarterPrompt
}) => {
  const isComposerBusy =
    !sessionId ||
    isStreaming ||
    isSynthesizingVision ||
    isCreatingSession ||
    isUploadingReference
  const hasPendingReferences = Boolean(pendingReferences.length)

  return (
    <section className="creative-direction-chat__studio-card creative-direction-chat__conversation">
      <div
        className={`creative-direction-chat__messages${
          !messages.length ? ' creative-direction-chat__messages--empty' : ''
        }`}
        ref={scrollRef}
      >
        {!messages.length ? (
          <div className="creative-direction-chat__starter-panel">
            <h4>How do you want to start?</h4>
            <div className="creative-direction-chat__starter-grid">
              {starterPrompts.map(item => (
                <button
                  type="button"
                  className={`creative-direction-chat__starter-card${
                    item.recommended
                      ? ' creative-direction-chat__starter-card--recommended'
                      : ''
                  }`}
                  key={item.key}
                  aria-label={`${item.actionLabel}: ${item.title}`}
                  disabled={
                    !sessionId ||
                    isStreaming ||
                    isSynthesizingVision ||
                    isCreatingSession
                  }
                  onClick={() => onStarterPrompt(item)}
                >
                  <div className="creative-direction-chat__starter-head">
                    <Icon type={item.icon} />
                  </div>
                  <strong>{item.title}</strong>
                  <small>{item.description}</small>
                  <div className="creative-direction-chat__starter-actions">
                    <span>
                      {item.actionLabel} <Icon type="arrow-right" />
                    </span>
                    {item.recommended ? <em>Recommended</em> : null}
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {timelinePanel ? (
          <div className="creative-direction-chat__inline-workflow">
            {timelinePanel}
          </div>
        ) : null}
      </div>

      {errorMessage ? (
        <Alert
          className="creative-direction-chat__alert creative-direction-chat__alert--inline"
          type="error"
          showIcon
          message={errorMessage}
        />
      ) : null}

      <div className="creative-direction-chat__composer">
        <Upload
          accept={getAcceptedAttribute(acceptedExtensions)}
          multiple
          showUploadList={false}
          beforeUpload={onAttachReference}
          disabled={isComposerBusy}
        >
          <Button
            className="creative-direction-chat__attach-button"
            disabled={isComposerBusy}
            aria-label="Attach image or video reference"
            title="Attach image or video reference"
          >
            <Icon type="paper-clip" />
          </Button>
        </Upload>
        <div className="creative-direction-chat__composer-field">
          {hasPendingReferences ? (
            <div className="creative-direction-chat__pending-files">
              {pendingReferences.map(item => (
                <div
                  className="creative-direction-chat__pending-file"
                  key={item.id}
                >
                  <span className="creative-direction-chat__pending-preview">
                    {item.previewUrl ? (
                      <img src={item.previewUrl} alt="" />
                    ) : (
                      <Icon
                        type={
                          getReferenceKind(item.file) === 'video'
                            ? 'video-camera'
                            : 'picture'
                        }
                      />
                    )}
                  </span>
                  <span className="creative-direction-chat__pending-name">
                    {item.file.name}
                    {formatFileSize(item.file.size) ? (
                      <small>{formatFileSize(item.file.size)}</small>
                    ) : null}
                  </span>
                  <button
                    type="button"
                    aria-label={`Remove ${item.file.name}`}
                    disabled={isComposerBusy}
                    onClick={() => onRemovePendingReference(item.id)}
                  >
                    <Icon type="close" />
                  </button>
                </div>
              ))}
            </div>
          ) : null}
          <TextArea
            value={draftMessage}
            rows={hasPendingReferences ? 3 : 2}
            disabled={isComposerBusy}
            placeholder={
              hasPendingReferences
                ? 'Add a note about the attached reference before sending...'
                : 'Type your answer or ask for a refinement...'
            }
            onChange={event => onDraftMessageChange(event.target.value)}
            onKeyDown={onKeyDown}
          />
        </div>
        <Button
          type="primary"
          loading={isUploadingReference}
          disabled={!draftMessage.trim() || isComposerBusy}
          onClick={onSubmit}
        >
          Send
          <Icon type="arrow-right" />
        </Button>
      </div>
    </section>
  )
}

const CreativeDirectionChat = ({
  sessionId,
  messages,
  metadata,
  phaseSteps,
  phaseData,
  creativeGuideline,
  progressPercent,
  creativeStatement,
  acceptedExtensions,
  isCreatingSession,
  isStreaming,
  isSynthesizingVision,
  isUploadingReference,
  isLockingVision,
  isVisionLocked,
  isFinalized,
  phaseBusyAction,
  error,
  onSendMessage,
  onSynthesizeVision,
  onLockVision,
  onExtractPhase,
  onVerifyEntity,
  onVerifyAllPhase,
  onGeneratePhase,
  onRefreshGenerationStatus,
  onAdvancePhase,
  onProposeStoryboardPlan,
  onApproveStoryboardPlan,
  onFinalizeCreativeDirection,
  onStartPrompt,
  onRetryCreateSession
}) => {
  const [draftMessage, setDraftMessage] = useState('')
  const [pendingReferences, setPendingReferences] = useState([])
  const [isFinalizedReportOpen, setIsFinalizedReportOpen] = useState(false)
  const scrollRef = useRef(null)
  const pendingReferencesRef = useRef([])
  const currentPhase = metadata.currentPhase || 'vision'
  const [expandedPhases, setExpandedPhases] = useState({})
  const isVisionPhase = currentPhase === 'vision'

  const togglePhaseDetails = phase => {
    setExpandedPhases(prev => ({
      ...prev,
      [phase]: !prev[phase]
    }))
  }

  useEffect(() => {
    pendingReferencesRef.current = pendingReferences
  }, [pendingReferences])

  useEffect(
    () => () => {
      revokePendingReferencePreviews(pendingReferencesRef.current)
    },
    []
  )

  useEffect(() => {
    const scrollToBottom = () => {
      if (scrollRef.current) {
        const scrollContainer = scrollRef.current.closest('.video-generation')

        if (scrollContainer) {
          scrollContainer.scrollTop = scrollContainer.scrollHeight
          return
        }

        scrollRef.current.scrollIntoView({
          block: 'end',
          behavior: 'smooth'
        })
      }
    }

    const frameId = window.requestAnimationFrame(scrollToBottom)
    const timeoutId = window.setTimeout(scrollToBottom, 120)

    return () => {
      window.cancelAnimationFrame(frameId)
      window.clearTimeout(timeoutId)
    }
  }, [
    creativeStatement,
    currentPhase,
    isStreaming,
    isSynthesizingVision,
    messages,
    phaseData,
    phaseBusyAction,
    error
  ])

  const handleAttachReference = file => {
    if (!file) return false

    if (!isAcceptedReferenceFile(file, acceptedExtensions)) {
      antMessage.error('Attach an image or video reference file.')
      return false
    }

    if (file.size > MAX_REFERENCE_FILE_SIZE) {
      antMessage.error('Reference files must be 50 MB or less.')
      return false
    }

    const fileKey =
      file.uid || `${file.name}-${file.size}-${file.lastModified || Date.now()}`
    const previewUrl =
      getReferenceKind(file) === 'image' && window.URL?.createObjectURL
        ? window.URL.createObjectURL(file)
        : ''

    setPendingReferences(prev => {
      if (prev.some(item => item.fileKey === fileKey)) {
        if (previewUrl && window.URL?.revokeObjectURL) {
          window.URL.revokeObjectURL(previewUrl)
        }
        return prev
      }

      return [
        ...prev,
        {
          id: `reference-${fileKey}`,
          fileKey,
          file,
          previewUrl
        }
      ]
    })

    return false
  }

  const handleRemovePendingReference = referenceId => {
    setPendingReferences(prev =>
      prev.filter(item => {
        const shouldKeep = item.id !== referenceId
        if (!shouldKeep && item.previewUrl && window.URL?.revokeObjectURL) {
          window.URL.revokeObjectURL(item.previewUrl)
        }
        return shouldKeep
      })
    )
  }

  const handleSubmit = async () => {
    const message = draftMessage.trim()
    if (
      !sessionId ||
      !message ||
      isStreaming ||
      isSynthesizingVision ||
      isCreatingSession ||
      isUploadingReference
    ) {
      return
    }

    const referencesToUpload = pendingReferences

    setDraftMessage('')

    if (referencesToUpload.length) {
      const uploadedReferenceIds = referencesToUpload.map(item => item.id)
      setPendingReferences(prev =>
        prev.filter(item => !uploadedReferenceIds.includes(item.id))
      )
      revokePendingReferencePreviews(referencesToUpload)
    }

    await onSendMessage(message, {
      files: referencesToUpload.map(item => item.file)
    })
  }

  const handleKeyDown = event => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSubmit()
    }
  }

  const handleStarterPrompt = item => {
    if (isStreaming || isSynthesizingVision || isCreatingSession || !sessionId) {
      return
    }

    if (item.action === 'start') {
      onStartPrompt()
      return
    }

    onSendMessage(item.message)
  }

  if (isCreatingSession) {
    return (
      <div className="creative-direction-chat__loading">
        <Spin size="large" />
        <div>
          <h3>Preparing AI</h3>
          <p>Starting a creative direction session for this story.</p>
        </div>
      </div>
    )
  }

  if (!sessionId && error) {
    return (
      <div className="creative-direction-chat__empty">
        <Icon type="warning" />
        <h3>Creative direction could not start</h3>
        <p>{getFriendlyErrorMessage(error)}</p>
        <Button type="primary" onClick={onRetryCreateSession}>
          Try again
        </Button>
      </div>
    )
  }

  const currentPhaseIndex = getPhaseIndex(phaseSteps, currentPhase)
  const visibleWorkflowPhases = phaseSteps.slice(1, currentPhaseIndex + 1)
  const hasConversation = messages.some(item =>
    String(item.content || '').trim()
  )
  const hasStreamingMessage = messages.some(item => item.streaming)
  const getMessagesForPhase = phase =>
    messages.filter(item => getMessagePhase(item) === phase)
  const shouldShowTypingForPhase = phase =>
    phase === currentPhase && isStreaming && !hasStreamingMessage
  const shouldShowVisionTimeline =
    hasConversation || Boolean(creativeStatement) || isSynthesizingVision

  const timelinePanel =
    isVisionPhase && !shouldShowVisionTimeline ? null : isVisionPhase ? (
      <>
        <MessageList
          messages={getMessagesForPhase('vision')}
          showTyping={shouldShowTypingForPhase('vision')}
        />
        <VisionDirectionCard
          creativeStatement={creativeStatement}
          hasConversation={hasConversation}
          isStreaming={isStreaming}
          isSynthesizingVision={isSynthesizingVision}
          isLockingVision={isLockingVision}
          isVisionLocked={isVisionLocked}
          isArchived={false}
          onSynthesizeVision={onSynthesizeVision}
          onLockVision={onLockVision}
        />
      </>
    ) : (
      <>
        {expandedPhases.vision ? (
          <>
            <MessageList messages={getMessagesForPhase('vision')} />
            <VisionDirectionCard
              creativeStatement={creativeStatement}
              hasConversation
              isStreaming={false}
              isSynthesizingVision={false}
              isLockingVision={false}
              isVisionLocked={isVisionLocked}
              isArchived
              onSynthesizeVision={onSynthesizeVision}
              onLockVision={onLockVision}
            />
            <div className="creative-direction-chat__phase-action-row">
              <Button size="small" onClick={() => togglePhaseDetails('vision')}>
                Hide details
              </Button>
            </div>
          </>
        ) : (
          <VisionSummaryCard
            creativeStatement={creativeStatement}
            isVisionLocked={isVisionLocked}
            onTogglePhase={() => togglePhaseDetails('vision')}
          />
        )}
        {visibleWorkflowPhases.map((phaseStep, index) => {
          const phaseKey = phaseStep.key
          const isPhaseActive = phaseKey === currentPhase
          const isPhaseExpanded = Boolean(expandedPhases[phaseKey])
          const shouldExpandPhase = isPhaseActive || isPhaseExpanded

          return (
            <React.Fragment key={phaseKey}>
              <WorkflowStepDivider
                stepNumber={index + 2}
                label={getPhaseView(phaseKey).label}
              />
              {shouldExpandPhase ? (
                <MessageList
                  messages={getMessagesForPhase(phaseKey)}
                  showTyping={shouldShowTypingForPhase(phaseKey)}
                />
              ) : null}
              <PhaseWorkspace
                currentPhase={phaseKey}
                phaseSteps={phaseSteps}
                phaseData={phaseData}
                creativeGuideline={creativeGuideline}
                isFinalized={isFinalized}
                isActivePhase={isPhaseActive}
                isSelectedPhase={isPhaseExpanded}
                showActions={
                  isPhaseActive ||
                  (phaseKey === 'storyboard' && isPhaseExpanded)
                }
                phaseBusyAction={phaseBusyAction}
                onExtractPhase={onExtractPhase}
                onVerifyEntity={onVerifyEntity}
                onVerifyAllPhase={onVerifyAllPhase}
                onGeneratePhase={onGeneratePhase}
                onRefreshGenerationStatus={onRefreshGenerationStatus}
                onAdvancePhase={onAdvancePhase}
                onProposeStoryboardPlan={onProposeStoryboardPlan}
                onApproveStoryboardPlan={onApproveStoryboardPlan}
                onFinalizeCreativeDirection={onFinalizeCreativeDirection}
                onViewFinalizedReport={() => setIsFinalizedReportOpen(true)}
                onTogglePhase={() => togglePhaseDetails(phaseKey)}
              />
            </React.Fragment>
          )
        })}
      </>
    )

  return (
    <div className="creative-direction-chat">
      <ProgressPreview
        phaseSteps={phaseSteps}
        currentPhase={currentPhase}
        expandedPhases={expandedPhases}
        progressPercent={progressPercent}
        isFinalized={isFinalized}
        onPhaseSelect={togglePhaseDetails}
      />

      <main className="creative-direction-chat__main">
        <StageHeader />

        <ConversationCard
          sessionId={sessionId}
          messages={messages}
          timelinePanel={timelinePanel}
          scrollRef={scrollRef}
          errorMessage={
            error && sessionId ? getFriendlyErrorMessage(error) : ''
          }
          isCreatingSession={isCreatingSession}
          isStreaming={isStreaming}
          isSynthesizingVision={isSynthesizingVision}
          isUploadingReference={isUploadingReference}
          acceptedExtensions={acceptedExtensions}
          pendingReferences={pendingReferences}
          draftMessage={draftMessage}
          onDraftMessageChange={setDraftMessage}
          onAttachReference={handleAttachReference}
          onRemovePendingReference={handleRemovePendingReference}
          onSubmit={handleSubmit}
          onKeyDown={handleKeyDown}
          onStarterPrompt={handleStarterPrompt}
        />
      </main>

      <FinalizedReportModal
        visible={isFinalizedReportOpen}
        report={creativeGuideline}
        onClose={() => setIsFinalizedReportOpen(false)}
      />
    </div>
  )
}

export default CreativeDirectionChat
